/**
 * Pathless layout route for unauthenticated screens (login, register,
 * forgot/reset, 2FA verify/setup, workspace picker).
 *
 * The underscore prefix marks this as a TanStack Router "pathless
 * layout" — the segment does NOT appear in the URL but every child
 * file inherits this shell. So `_auth/login.tsx` is reachable at
 * `/login`, `_auth/register.tsx` at `/register`, etc.
 *
 * Visual: a centred card floating on an on-brand abstract indigo
 * backdrop (concentrated at the top), with the wordmark on top and a
 * tagline below. A top-right close (×) + Esc return to the public
 * marketing home — the dashboard SPA root `/` only bounces back to
 * /login, so this is the only real escape hatch to the home page. For
 * someone already signed in to a workspace (opening /2fa-setup from the
 * ICC pages) they go back into the app instead: the `?next=` page, else
 * the workspace home (lib/auth/auth-close.ts).
 *
 * The shell makes NO auth decisions — that lives in the individual auth
 * pages and in the gated layout (which bounces unauthenticated traffic
 * here via /login).
 */

import { useEffect, type MouseEvent } from "react";
import { Outlet, createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { BrandLogo } from "@/components/shared/brand-logo";
import { useAuth } from "@/lib/auth";
import { authCloseTarget } from "@/lib/auth/auth-close";

export const Route = createFileRoute("/_auth")({
  component: AuthLayout,
});

// The marketing site is a separate app on a different domain. Baked at
// build time per env (dashboard/fly.<env>.toml); dev falls back to prod.
const MARKETING_URL = import.meta.env.VITE_MARKETING_URL ?? "https://workright.in";

function AuthLayout() {
  const { user, identityOnly } = useAuth();
  const navigate = useNavigate();
  const next = useLocation({
    select: (l) => (l.search as Record<string, unknown>).next,
  });
  const close = authCloseTarget(
    user,
    identityOnly,
    typeof next === "string" ? next : undefined,
    MARKETING_URL,
  );
  const closeHref = close.internal ? close.to : close.href;
  const closeLabel = close.internal ? "Back to WorkRight" : "Back to Home";

  // An in-app target navigates inside the SPA (no reload).
  const goClose = (e?: MouseEvent) => {
    if (!close.internal) return;
    e?.preventDefault();
    void navigate({ to: close.to });
  };

  // Esc does what the × does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (close.internal) void navigate({ to: close.to });
      else window.location.href = close.href;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close.internal, closeHref, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-start overflow-hidden px-4 pb-10 pt-16 sm:justify-center sm:py-10 sm:pb-24">
      {/* Abstract on-brand backdrop — decorative, concentrated at the top.
          pointer-events-none + aria-hidden so it never interferes with
          the form or assistive tech; placed behind everything via -z-10. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent" />
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="absolute left-1/2 top-8 h-56 w-[30rem] -translate-x-1/2 rounded-full bg-violet-300/15 blur-3xl" />
      </div>

      {/* Close → the marketing home, or back into the app when signed in. */}
      <a
        href={closeHref}
        onClick={goClose}
        aria-label={closeLabel}
        title={`${closeLabel} (Esc)`}
        className="absolute right-4 top-4 inline-flex h-9 w-9 items-center [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 justify-center rounded-full border border-border/60 bg-background/70 text-muted-foreground shadow-sm backdrop-blur transition hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">{closeLabel}</span>
      </a>

      <div className="w-full max-w-md">
        <a
          href={closeHref}
          onClick={goClose}
          className="mb-8 flex justify-center"
          aria-label="WorkRight home"
        >
          <BrandLogo />
        </a>
        <Outlet />
        <p className="mt-8 text-center text-xs text-muted-foreground">
          HR &amp; compliance for Indian companies.
        </p>
      </div>
    </div>
  );
}
